import { createI18n } from "vue-i18n";
import { i18nUtils } from "@utils/i18n";
import { appLocalStorage } from "@utils/app.storage";
import dateTimeFormats from "@locales/formats/date.time.formats";
import numberFormats from "@locales/formats/number.formats";
import { isEmpty } from "@utils/var.check";

// https://github.com/intlify/bundle-tools/tree/main/packages/vite-plugin-vue-i18n
// https://levelup.gitconnected.com/how-to-load-i18n-locales-asynchronously-in-vue-3-vite-9e7301c52f19

const loadedLocales = [];
const loadedLocaleModule = {};
const locale = i18nUtils.getStartingLocale(appLocalStorage.getItem("locale"));
export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale,
  fallbackLocale: i18nUtils.getFallbackLocale(),
  messages: [], // async load the messages
  dateTimeFormats,
  numberFormats,
});

const commonLocalMessagePromise = [
  loadLocaleMessages("common"),
  loadLocaleMessages("common-aria-label"),
];

const localePromise = Promise.all(commonLocalMessagePromise).then(async () => {
  loadedLocales.push(locale);
});

function importLocaleMessages(locale, module) {
  return import(`../assets/locales/messages/${locale}/${module}.json`);
}

function addLoadedLocaleModule(module) {
  // add key if does not exist
  if (isEmpty(loadedLocaleModule[getCurrentLocale()])) {
    loadedLocaleModule[getCurrentLocale()] = [];
  }

  // tracking of loaded modules
  loadedLocaleModule[getCurrentLocale()].push(module);
}

export function changeLocale(newLocale) {
  if (loadedLocales.includes(newLocale) && getCurrentLocale() === newLocale) return;
  setCurrentLocale(newLocale);
  appLocalStorage.setItem("locale", newLocale);
  loadLocaleMessages("common").then(() => loadedLocales.push(newLocale));
}

function processLocaleMessagesAfterLoad(module, messages) {
  if (!isEmpty(loadedLocaleModule[getCurrentLocale()]) && isOverlappingLocaleModuleLoaded(module)) {
    // overlapping modules need to be removed so that it will not resolve due to locale being previously loaded,
    // and i18n.mergeLocaleMessage will run again to overwrite overlapping keys.
    removeOverlappingLoadedLocaleModule(module);
  }
  addLoadedLocaleModule(module);
  i18n.global.mergeLocaleMessage(getCurrentLocale(), messages.default);
  i18nUtils.setDocumentLang(getCurrentLocale());
}

export function loadLocaleMessages(module) {
  // if language's locale module has already been imported, return
  if (
    !isEmpty(loadedLocaleModule[getCurrentLocale()]) &&
    loadedLocaleModule[getCurrentLocale()].includes(module)
  )
    return Promise.resolve(getCurrentLocale());

  return importLocaleMessages(getCurrentLocale(), module).then((messages) => {
    processLocaleMessagesAfterLoad(module, messages);
    return Promise.resolve(getCurrentLocale());
  });
}

export function isOverlappingLocaleModuleLoaded(module) {
  // Compare main module based on the first word before fullstop
  const moduleToCheck = module.split(".", 1);
  if (moduleToCheck[0] === module) return false;
  const found = loadedLocaleModule[getCurrentLocale()].find((loadedModule) =>
    loadedModule.startsWith(moduleToCheck),
  );
  return !!found;
}

export function removeOverlappingLoadedLocaleModule(module) {
  // Remove overlapping module from originally loaded modules
  const overlappingModule = module.split(".", 1);
  loadedLocaleModule[getCurrentLocale()] = loadedLocaleModule[getCurrentLocale()].filter(
    (module) => !module.startsWith(overlappingModule),
  );
}

export function getCurrentLocale() {
  return i18n.global.locale.value;
}

export function setCurrentLocale(locale) {
  i18n.global.locale.value = locale;
}

export function isLocaleReady() {
  return localePromise;
}

export default i18n.global;
